import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { useRequestResetPassword } from '@/app/module/auth/hooks/use-request-reset-password'

type RequestResetPasswordValues = { email: string }

const RequestResetPasswordForm: React.FC = () => {
  const navigate = useNavigate()
  const [requestResetPassword, { isLoading }] = useRequestResetPassword()

  const handleSubmit = async (data: RequestResetPasswordValues) => {
    const success = await requestResetPassword(data)
    if (success) {
      navigate('/login')
    }
  }

  return (
    <StyledCard>
      <StyledCardContent>
        <StyledIcon>account_circle</StyledIcon>
        <Typography color="textSecondary" variant="caption" mb={1}>
          Enter your email address below, and we'll send you an email allowing you to reset it.
        </Typography>
        <Form
          defaultValues={defaultValues}
          fields={fields}
          schema={schema}
          onSubmit={handleSubmit}
          renderActions={({ isDirty }) => (
            <StyledActions>
              <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
                Reset Password
              </SubmitButton>
            </StyledActions>
          )}
        />
      </StyledCardContent>
    </StyledCard>
  )
}

const defaultValues = {
  email: '',
}

const fields = {
  email: { fieldProps: { label: 'Email', name: 'email', type: 'email', required: true }, type: 'email' },
} as const

const schema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email({ message: 'Invalid email address' }),
})

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: 'calc(60px + 5%)',
  marginBottom: '3rem',
})

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '2rem',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  margin: 'auto',
  marginBottom: '15px',
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

const StyledActions = styled(CardActions)({
  justifyContent: 'flex-end',
  paddingBottom: '1rem',
  paddingLeft: 0,
  paddingRight: 0,
})

export default RequestResetPasswordForm
